import PropTypes from 'prop-types';

import DashboardCard from '../DashboardCard';
import { useWidgetActions } from './hooks/useWidgetActions';
import { useWidgetContent } from './hooks/useWidgetContent';
import { useWidgetData } from './hooks/useWidgetData';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import { useWidgetFilterStrip } from '../../hooks/useSingleFilter';
import { useEventsByScheduleContext } from '../../contexts/EventsByScheduleContext';
import { useMemo } from 'react';

const DashboardWidget = ({ widget, onEdit, refresh, filters, config }) => {
  const { activeFilterWidgetId } = useEventsByScheduleContext();
  const isFilterPanelActive = activeFilterWidgetId === widget.id;
  const {
    activeSingleFilter,
    filterActiveTagsProps,
    openWidgetFiltersPanel,
    filtersForThisWidget,
    widgetTableFilterHandlers,
  } = useWidgetFilterStrip(widget, config);

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      widgetFilterValues: filtersForThisWidget?.filters ?? null,
    }),
    [filters, filtersForThisWidget?.filters],
  );

  const widgetData = useWidgetData({
    widget,
    filters: mergedFilters,
    config,
    refresh,
    widgetTableFilterHandlers,
  });

  const widgetActions = useWidgetActions({
    widget,
    config,
    onEdit,
  });

  const widgetContent = useWidgetContent({
    widget: { ...widget, filters },
    config,
    data: widgetData.data,
    loading: widgetData.loading,
    additionalState: widgetData.additionalState,
    actions: widgetActions,
  });

  return (
    <DashboardCard
      key={widget.id}
      title={widget.title}
      subTitle={widgetContent.subTitle}
      actionButtons={widgetContent.actionButtons}
      className={config.additionalProps?.className}
      downloading={widgetActions.downloading}
      highlighted={isFilterPanelActive}
    >
      {activeSingleFilter && (
        <FilterActiveTags.Container className="gx-pl-0 gx-pb-1 gx-border-0" panelAnchor={false}>
          <FilterActiveTags.Bar>
            <FilterActiveTags.ScrollArea>
              <FilterActiveTags onAddFilter={openWidgetFiltersPanel} {...filterActiveTagsProps} />
            </FilterActiveTags.ScrollArea>
          </FilterActiveTags.Bar>
        </FilterActiveTags.Container>
      )}
      {widgetContent.content}
    </DashboardCard>
  );
};

DashboardWidget.propTypes = {
  widget: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  refresh: PropTypes.bool.isRequired,
  filters: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
};

export default DashboardWidget;
